import { useState, useEffect, useCallback } from 'react';
import {
  Shield, Users, Building2, Briefcase, CheckCircle, XCircle, Clock,
  Loader2, ChevronDown, ChevronUp, Eye, FileText, Mail, Phone,
  AlertCircle, BarChart3, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGE = {
  pending: { label: 'En attente', cls: 'bg-amber-50 text-amber-600', icon: Clock },
  approved: { label: 'Approuvée', cls: 'bg-green-50 text-green-600', icon: CheckCircle },
  rejected: { label: 'Refusée', cls: 'bg-red-50 text-red-600', icon: XCircle },
};

const AdminEspaceEmploiTab = ({ token }) => {
  const [subtab, setSubtab] = useState('pending');
  const [stats, setStats] = useState(null);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, candsRes, empsRes, appsRes] = await Promise.all([
        axios.get(`${API}/espace-emploi/admin/stats`, { headers }),
        axios.get(`${API}/espace-emploi/admin/pending-offers`, { headers }),
        axios.get(`${API}/espace-emploi/admin/all-candidates`, { headers }),
        axios.get(`${API}/espace-emploi/admin/all-employers`, { headers }),
        axios.get(`${API}/espace-emploi/admin/all-applications`, { headers }),
      ]);
      setStats(statsRes.data);
      setPendingOffers(pendingRes.data);
      setCandidates(candsRes.data);
      setEmployers(empsRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement Espace Emploi');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveOffer = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/espace-emploi/admin/offers/${id}/approve`, {}, { headers });
      toast.success('Offre approuvée et publiée');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  const rejectOffer = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/espace-emploi/admin/offers/${id}/reject`, {}, { headers });
      toast.success('Offre rejetée');
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;

  const SUBTABS = [
    { id: 'pending', label: 'Modération', icon: Shield, count: stats?.pending_offers },
    { id: 'candidates', label: 'Candidats', icon: Users, count: stats?.candidates },
    { id: 'employers', label: 'Employeurs', icon: Building2, count: stats?.employers },
    { id: 'applications', label: 'Candidatures', icon: Mail, count: stats?.applications },
  ];

  return (
    <div data-testid="admin-espace-emploi">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Candidats', value: stats?.candidates || 0, color: 'text-teal-600' },
          { label: 'Employeurs', value: stats?.employers || 0, color: 'text-amber-600' },
          { label: 'En attente', value: stats?.pending_offers || 0, color: 'text-orange-600' },
          { label: 'Approuvées', value: stats?.approved_offers || 0, color: 'text-green-600' },
          { label: 'Candidatures', value: stats?.applications || 0, color: 'text-blue-600' },
          { label: 'Abonnements', value: stats?.active_subscriptions || 0, color: 'text-purple-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border border-slate-200 p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-5 bg-slate-50 rounded-lg p-1">
        {SUBTABS.map(st => (
          <button key={st.id} onClick={() => setSubtab(st.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all flex-1 justify-center ${
              subtab === st.id ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500 hover:text-slate-700'
            }`} data-testid={`subtab-${st.id}`}>
            <st.icon className="w-3.5 h-3.5" /> {st.label}
            {st.count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${subtab === st.id ? 'bg-teal-100 text-teal-700' : 'bg-slate-200'}`}>{st.count}</span>}
          </button>
        ))}
      </div>

      {/* SUBTAB: Moderation */}
      {subtab === 'pending' && (
        <div data-testid="subtab-content-pending">
          {pendingOffers.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <CheckCircle className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucune offre en attente de validation.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingOffers.map(offer => {
                const isOpen = expandedId === offer.id;
                return (
                  <div key={offer.id} className="bg-white rounded-xl border border-slate-200">
                    <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(isOpen ? null : offer.id)}>
                      <div>
                        <h3 className="font-bold text-sm text-slate-800">{offer.title}</h3>
                        <p className="text-xs text-slate-500">{offer.company} — {offer.location} — {offer.contract_type}</p>
                        {offer.employer_info && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Par: {offer.employer_info.company_name || offer.contact_email}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">En attente</span>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                        <p className="text-sm text-slate-600 mb-3">{offer.summary}</p>
                        {offer.missions?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs font-semibold text-slate-700 mb-1">Missions :</p>
                            <ul className="text-xs text-slate-600 space-y-0.5">
                              {offer.missions.map((m, i) => <li key={i}>• {m}</li>)}
                            </ul>
                          </div>
                        )}
                        {offer.attached_file && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 text-xs text-slate-600">
                            <FileText className="w-3.5 h-3.5" /> Fichier joint: {offer.attached_file?.filename || 'Document'}
                          </div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <Button onClick={() => approveOffer(offer.id)} disabled={actionLoading === offer.id}
                            size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`approve-${offer.id}`}>
                            {actionLoading === offer.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approuver et publier</>}
                          </Button>
                          <Button onClick={() => rejectOffer(offer.id)} disabled={actionLoading === offer.id}
                            size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 text-xs" data-testid={`reject-${offer.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SUBTAB: Candidates */}
      {subtab === 'candidates' && (
        <div data-testid="subtab-content-candidates">
          {candidates.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucun candidat inscrit.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map((c, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{c.user?.name || 'Anonyme'}</p>
                    <p className="text-xs text-slate-500">{c.headline || 'Pas de titre'} — {c.sector || 'Secteur non renseigne'}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                      {c.user?.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.user.email}</span>}
                      <span>Disponibilité: {c.availability === 'immediat' ? 'Immédiat' : c.availability || '-'}</span>
                      <span>Contact: {c.contact_préférence === 'direct' ? 'Direct' : 'Via AERH'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(c.skills || []).slice(0, 3).map((s, j) => (
                      <span key={j} className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SUBTAB: Employers */}
      {subtab === 'employers' && (
        <div data-testid="subtab-content-employers">
          {employers.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucun employeur inscrit.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {employers.map((e, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{e.company_name || e.user?.company || 'Entreprise'}</p>
                    <p className="text-xs text-slate-500">{e.sector || '-'} — {e.user?.email}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="bg-slate-100 px-2 py-0.5 rounded-full">{e.offer_count || 0} offre(s)</span>
                    {e.subscription ? (
                      <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">Abonné</span>
                    ) : (
                      <span className="bg-slate-50 text-slate-400 px-2 py-0.5 rounded-full">Gratuit</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SUBTAB: Applications */}
      {subtab === 'applications' && (
        <div data-testid="subtab-content-applications">
          {applications.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucune candidature pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {applications.map(app => (
                <div key={app.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{app.candidate_name}</p>
                    <p className="text-xs text-slate-500">Pour : {app.offer_title} ({app.company})</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(app.created_at).toLocaleDateString('fr-FR')} — Contact: {app.contact_préférence === 'direct' ? 'Direct' : 'Via AERH'}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    app.status === 'new' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {app.status === 'new' ? 'Nouvelle' : app.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminEspaceEmploiTab;
